import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { profitMarginService } from '../../services/financeService';
import { ProfitMarginAnalysis } from '../../types/finance';
import { FaSave, FaTrash, FaPlus, FaArrowLeft, FaChartLine } from 'react-icons/fa';
import toast from 'react-hot-toast';

const ProfitMarginAnalyzer: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [analyses, setAnalyses] = useState<ProfitMarginAnalysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [currentAnalysis, setCurrentAnalysis] = useState<ProfitMarginAnalysis>({
    name: '',
    product_name: '',
    selling_price: 0,
    cost_of_goods: 0,
    overhead_costs: 0,
    gross_margin: 0,
    net_margin: 0,
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    loadAnalyses();
  }, [user, navigate]);

  const loadAnalyses = async () => {
    try {
      setLoading(true);
      const data = await profitMarginService.getAll();
      setAnalyses(data);
    } catch (error) {
      console.error('Error loading profit margin analyses:', error);
      toast.error('Failed to load profit margin analyses');
    } finally {
      setLoading(false);
    }
  };

  const calculateMargins = (analysis: ProfitMarginAnalysis): { gross: number; net: number } => {
    const { selling_price, cost_of_goods, overhead_costs } = analysis;
    
    // Gross margin = (Selling Price - Cost of Goods) / Selling Price
    const grossMargin = selling_price > 0 
      ? (selling_price - cost_of_goods) / selling_price 
      : 0;
    
    // Net margin = (Selling Price - Cost of Goods - Overhead) / Selling Price
    const netMargin = selling_price > 0 
      ? (selling_price - cost_of_goods - overhead_costs) / selling_price 
      : 0;
    
    return {
      gross: parseFloat(grossMargin.toFixed(4)),
      net: parseFloat(netMargin.toFixed(4))
    };
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    let parsedValue: string | number = value;
    
    // Parse numeric values
    if (['selling_price', 'cost_of_goods', 'overhead_costs', 'industry_average_margin'].includes(name)) {
      parsedValue = parseFloat(value) || 0;
    }
    
    setCurrentAnalysis(prev => ({
      ...prev,
      [name]: parsedValue
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const { gross, net } = calculateMargins(currentAnalysis);
      const analysisToSave = {
        ...currentAnalysis,
        user_id: user?.id,
        gross_margin: gross,
        net_margin: net
      };
      
      if (currentAnalysis.id) {
        await profitMarginService.update(currentAnalysis.id, analysisToSave);
        toast.success('Profit margin analysis updated successfully');
      } else {
        await profitMarginService.create(analysisToSave);
        toast.success('Profit margin analysis created successfully');
      }
      
      setShowForm(false);
      setCurrentAnalysis({
        name: '',
        product_name: '',
        selling_price: 0,
        cost_of_goods: 0,
        overhead_costs: 0,
        gross_margin: 0,
        net_margin: 0,
      });
      loadAnalyses();
    } catch (error) {
      console.error('Error saving profit margin analysis:', error);
      toast.error('Failed to save profit margin analysis');
    }
  };

  const handleEdit = (analysis: ProfitMarginAnalysis) => {
    setCurrentAnalysis(analysis);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this profit margin analysis?')) {
      try {
        await profitMarginService.delete(id);
        toast.success('Profit margin analysis deleted successfully');
        loadAnalyses();
      } catch (error) {
        console.error('Error deleting profit margin analysis:', error);
        toast.error('Failed to delete profit margin analysis');
      }
    }
  };

  // Function to determine if a margin is healthy based on industry standards
  const getMarginHealth = (margin: number): { status: string; color: string } => {
    if (margin <= 0) return { status: 'Loss', color: 'text-red-600' };
    if (margin < 0.15) return { status: 'Low', color: 'text-orange-500' };
    if (margin < 0.25) return { status: 'Average', color: 'text-yellow-500' };
    if (margin < 0.35) return { status: 'Good', color: 'text-green-500' };
    return { status: 'Excellent', color: 'text-green-700' };
  };

  if (loading) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  return (
    <section className="py-12 relative overflow-hidden">
      {/* Background with gradient and mesh */}
      <div className="absolute inset-0 bg-gray-50 z-0">
        <div className="absolute inset-0 opacity-30 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgdmlld0JveD0iMCAwIDYwIDYwIiBmaWxsPSJub25lIj48cGF0aCBkPSJNLjUuNWg2MHY2MEguNVYuNXptMzAgMTVhMTUgMTUgMCAwIDAtMTUgMTUgMTUgMTUgMCAwIDAgMTUgMTUgMTUgMTUgMCAwIDAgMTUtMTUgMTUgMTUgMCAwIDAtMTUtMTV6bTAgMGExNSAxNSAwIDAgMC0xNSAxNSAxNSAxNSAwIDAgMCAxNSAxNSAxNSAxNSAwIDAgMCAxNS0xNSAxNSAxNSAwIDAgMC0xNS0xNXoiIHN0cm9rZT0iIzIwMjAyMCIgc3Ryb2tlLW9wYWNpdHk9Ii4wNiIvPjwvc3ZnPg==')] bg-[size:50px_50px]"></div>
      </div>
      
      {/* Main content */}
      <div className="container relative z-10 mx-auto px-4">
        {/* Decorative elements */}
        <div className="absolute top-20 left-12 w-64 h-64 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
        <div className="absolute -bottom-32 right-12 w-64 h-64 bg-pink-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        
        <div className="mb-6">
          <button
            onClick={() => navigate('/finance')}
            className="flex items-center text-blue-600 hover:text-blue-800 transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow px-3 py-1 rounded-lg"
          >
            <FaArrowLeft className="mr-2" /> Back to Financial Tools
          </button>
        </div>
      
      <div className="relative overflow-hidden rounded-xl bg-white/20 backdrop-blur-lg border border-white/30 shadow-lg p-6 mb-8">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-400 to-blue-500 opacity-5"></div>
        <h1 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 inline-block mb-2">Profit Margin Analyzer</h1>
        <div className="h-1 w-32 mb-4 rounded-full bg-gradient-to-r from-blue-400 to-purple-500"></div>
        <p className="text-gray-700 mb-6">
          Calculate and visualize your profit margins to ensure your business is financially healthy.
        </p>
        
        <div className="relative overflow-hidden rounded-xl bg-blue-50/60 backdrop-blur-sm border border-blue-100/80 shadow-md p-5 mb-6">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-indigo-400 opacity-5"></div>
          <h2 className="text-xl font-semibold mb-2 flex items-center">
            <span className="w-8 h-8 rounded-lg mr-2 flex items-center justify-center text-white bg-gradient-to-br from-blue-400 to-indigo-500 shadow-md">
              <FaChartLine />
            </span>
            How it works
          </h2>
          <p className="mb-2">
            This tool helps you analyze the profitability of your products or services by calculating key margin metrics:
          </p>
          <ul className="list-disc pl-5 mb-2">
            <li><strong>Gross Margin</strong>: The percentage of revenue that exceeds the cost of goods sold (COGS)</li>
            <li><strong>Net Margin</strong>: The percentage of revenue that remains after accounting for all expenses</li>
          </ul>
          <p className="mb-2">
            Higher margins generally indicate a more profitable business, but what's considered "good" varies by industry.
          </p>
          <p>
            <strong>Typical healthy margins:</strong> Retail: 20-25% | Manufacturing: 25-35% | Software: 70-80% | Consulting: 40-60%
          </p>
        </div>
      </div>
      
      {!showForm ? (
        <div className="mb-6">
          <button
            onClick={() => {
              setCurrentAnalysis({
                name: '',
                product_name: '',
                selling_price: 0,
                cost_of_goods: 0,
                overhead_costs: 0,
                gross_margin: 0,
                net_margin: 0,
              });
              setShowForm(true);
            }}
            className="px-5 py-2.5 text-sm font-medium rounded-lg transition-all duration-300 bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg hover:shadow-blue-500/50 transform hover:-translate-y-0.5 flex items-center"
          >
            <FaPlus className="mr-2" /> Create New Profit Margin Analysis
          </button>
        </div>
      ) : (
        <div className="relative overflow-hidden rounded-xl bg-white/20 backdrop-blur-lg border border-white/30 shadow-lg p-6 mb-8">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-400 to-blue-500 opacity-5"></div>
          <h2 className="text-xl font-semibold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
            {currentAnalysis.id ? 'Edit Profit Margin Analysis' : 'Create New Profit Margin Analysis'}
          </h2>
          
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Analysis Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={currentAnalysis.name}
                  onChange={handleInputChange}
                  className="w-full p-2.5 bg-white/70 backdrop-blur-sm border border-gray-300/50 rounded-lg focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                  placeholder="e.g., Q1 2025 Product Line"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product/Service Name
                </label>
                <input
                  type="text"
                  name="product_name"
                  value={currentAnalysis.product_name}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  placeholder="e.g., Premium Subscription"
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Selling Price ($)
                </label>
                <input
                  type="number"
                  name="selling_price"
                  value={currentAnalysis.selling_price}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  placeholder="29.99"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cost of Goods Sold ($)
                </label>
                <input
                  type="number"
                  name="cost_of_goods"
                  value={currentAnalysis.cost_of_goods}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  placeholder="10.00"
                  min="0"
                  step="0.01"
                  required
                />
                <span className="text-xs text-gray-500">
                  Direct costs like materials and labor
                </span>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Overhead Costs ($)
                </label>
                <input
                  type="number"
                  name="overhead_costs"
                  value={currentAnalysis.overhead_costs}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  placeholder="5.00"
                  min="0"
                  step="0.01"
                  required
                />
                <span className="text-xs text-gray-500">
                  Indirect costs like rent, utilities, etc.
                </span>
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Industry Average Margin (Optional)
              </label>
              <input
                type="number"
                name="industry_average_margin"
                value={currentAnalysis.industry_average_margin || ''}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded-md"
                placeholder="0.25"
                min="0"
                max="1"
                step="0.01"
              />
              <span className="text-xs text-gray-500">
                Enter as decimal (e.g., 0.25 for 25%)
              </span>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes (Optional)
              </label>
              <textarea
                name="notes"
                value={currentAnalysis.notes || ''}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded-md"
                placeholder="Any additional information about this analysis"
                rows={3}
              />
            </div>
            
            <div className="flex justify-between">
              <div>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg flex items-center shadow-lg hover:shadow-blue-500/50 transition-all duration-300 transform hover:-translate-y-0.5 mr-2"
                >
                  <FaSave className="mr-2" /> {currentAnalysis.id ? 'Update' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="bg-gray-300/80 backdrop-blur-sm text-gray-700 px-5 py-2.5 rounded-lg hover:bg-gray-400/80 transition-all duration-300 transform hover:-translate-y-0.5"
                >
                  Cancel
                </button>
              </div>
              
              {currentAnalysis.id && (
                <button
                  type="button"
                  onClick={() => handleDelete(currentAnalysis.id!)}
                  className="px-5 py-2.5 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-lg flex items-center shadow-lg hover:shadow-red-500/50 transition-all duration-300 transform hover:-translate-y-0.5"
                >
                  <FaTrash className="mr-2" /> Delete
                </button>
              )}
            </div>
            
            {currentAnalysis.selling_price > 0 && (
              <div className="mt-6 p-5 relative overflow-hidden rounded-xl bg-green-50/70 backdrop-blur-sm border border-green-100/80 shadow-md">
                <div className="absolute inset-0 bg-gradient-to-br from-green-400 to-teal-400 opacity-5"></div>
                <h3 className="text-lg font-semibold mb-2 flex items-center">
                  <span className="w-7 h-7 rounded-lg mr-2 flex items-center justify-center text-white bg-gradient-to-br from-green-400 to-teal-500 shadow-md">
                    <FaChartLine className="text-sm" />
                  </span>
                  Margin Preview
                </h3>
                
                {(() => {
                  const { gross, net } = calculateMargins(currentAnalysis);
                  const grossHealth = getMarginHealth(gross);
                  const netHealth = getMarginHealth(net);
                  
                  return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-gray-700 mb-1">
                          <strong>Gross Margin:</strong> 
                          <span className={`ml-2 font-bold ${grossHealth.color}`}>
                            {(gross * 100).toFixed(1)}%
                          </span>
                          <span className={`ml-2 text-sm ${grossHealth.color}`}>({grossHealth.status})</span>
                        </p>
                        <p className="text-sm text-gray-500">
                          Revenue after direct costs
                        </p>
                      </div>
                      
                      <div>
                        <p className="text-gray-700 mb-1">
                          <strong>Net Margin:</strong> 
                          <span className={`ml-2 font-bold ${netHealth.color}`}>
                            {(net * 100).toFixed(1)}%
                          </span>
                          <span className={`ml-2 text-sm ${netHealth.color}`}>({netHealth.status})</span>
                        </p>
                        <p className="text-sm text-gray-500">
                          Revenue after all costs
                        </p>
                      </div>
                    </div>
                  );
                })()} 
                
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-sm text-gray-600">
                    <strong>Revenue Breakdown:</strong> For each $1.00 in sales:
                  </p>
                  
                  {(() => {
                    const { gross, net } = calculateMargins(currentAnalysis);
                    const cogs = currentAnalysis.cost_of_goods / currentAnalysis.selling_price;
                    const overhead = currentAnalysis.overhead_costs / currentAnalysis.selling_price;
                    
                    return (
                      <div className="mt-2 h-6 w-full bg-gray-200 rounded-full overflow-hidden">
                        <div className="flex h-full">
                          <div 
                            className="bg-red-500 h-full flex items-center justify-center text-xs text-white"
                            style={{ width: `${cogs * 100}%` }}
                            title={`Cost of Goods: $${currentAnalysis.cost_of_goods.toFixed(2)}`}
                          >
                            {cogs >= 0.1 && `${(cogs * 100).toFixed(0)}%`}
                          </div>
                          <div 
                            className="bg-yellow-500 h-full flex items-center justify-center text-xs text-white"
                            style={{ width: `${overhead * 100}%` }}
                            title={`Overhead: $${currentAnalysis.overhead_costs.toFixed(2)}`}
                          >
                            {overhead >= 0.1 && `${(overhead * 100).toFixed(0)}%`}
                          </div>
                          <div 
                            className="bg-green-500 h-full flex items-center justify-center text-xs text-white"
                            style={{ width: `${net * 100}%` }}
                            title={`Net Profit: $${(currentAnalysis.selling_price - currentAnalysis.cost_of_goods - currentAnalysis.overhead_costs).toFixed(2)}`}
                          >
                            {net >= 0.1 && `${(net * 100).toFixed(0)}%`}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  
                  <div className="flex text-xs mt-1 text-gray-600 justify-between">
                    <span>Cost of Goods</span>
                    <span>Overhead</span>
                    <span>Net Profit</span>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>
      )}
      
      {analyses.length > 0 ? (
        <div className="relative overflow-hidden rounded-xl bg-white/20 backdrop-blur-lg border border-white/30 shadow-lg p-6">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-indigo-500 opacity-5"></div>
          <h2 className="text-xl font-semibold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Your Profit Margin Analyses</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-blue-50/50 backdrop-blur-sm">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    COGS
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Gross Margin
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Net Margin
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white/50 backdrop-blur-sm divide-y divide-gray-200/70">
                {analyses.map((analysis) => {
                  const grossHealth = getMarginHealth(analysis.gross_margin);
                  const netHealth = getMarginHealth(analysis.net_margin);
                  
                  return (
                    <tr key={analysis.id} className="hover:bg-white/80 transition-all duration-300">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {analysis.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {analysis.product_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        ${analysis.selling_price.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        ${analysis.cost_of_goods.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={grossHealth.color}>
                          {(analysis.gross_margin * 100).toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={netHealth.color}>
                          {(analysis.net_margin * 100).toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEdit(analysis)}
                          className="text-blue-600 hover:text-blue-900 mr-3 hover:underline transition-all duration-300"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(analysis.id!)}
                          className="text-red-600 hover:text-red-900 hover:underline transition-all duration-300"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        !showForm && (
          <div className="relative overflow-hidden rounded-xl bg-white/20 backdrop-blur-lg border border-white/30 shadow-lg p-8 text-center">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-indigo-500 opacity-5"></div>
            <p className="text-gray-600 mb-5">You haven't created any profit margin analyses yet.</p>
            <button
              onClick={() => setShowForm(true)}
              className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg shadow-lg hover:shadow-blue-500/50 transition-all duration-300 transform hover:-translate-y-0.5"
            >
              Create Your First Profit Margin Analysis
            </button>
          </div>
        )
      )}
      </div>
    </section>
  );
};

export default ProfitMarginAnalyzer;
